import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';

import { SMATCHY_APP_STORE_ID, StorePlatform } from '../../../../../core/domains/app/minimum-version';

/**
 * The screen an application too old to be supported opens on, with no way out
 * but the store.
 *
 * On Android it first asks Google Play for its own update screen, which
 * downloads and installs without leaving the application. Play only offers it
 * when it knows of a newer version and allows the immediate kind; in every
 * other case, and whenever it fails, the store page opens instead. A button
 * that does nothing would leave the member stuck on a screen they cannot close.
 */
@Component({
  selector: 'smatchy-forced-update',
  templateUrl: './forced-update.component.html',
  imports: [IonicModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForcedUpdateComponent {
  @Input() platform: StorePlatform = 'ios';
  @Input() installed = '';
  @Input() required = '';

  busy = signal(false);

  async update(): Promise<void> {
    this.busy.set(true);
    try {
      if (this.platform === 'android' && (await this.updateInsidePlay())) {
        return;
      }
      await this.openStore();
    } catch (error) {
      console.warn('[ForcedUpdate] the store did not open', error);
    } finally {
      this.busy.set(false);
    }
  }

  /** Whether Google Play took the update over. */
  private async updateInsidePlay(): Promise<boolean> {
    try {
      const info = await AppUpdate.getAppUpdateInfo();
      if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE || !info.immediateUpdateAllowed) {
        return false;
      }
      await AppUpdate.performImmediateUpdate();
      return true;
    } catch {
      return false;
    }
  }

  private openStore(): Promise<void> {
    return AppUpdate.openAppStore(this.platform === 'ios' ? { appId: SMATCHY_APP_STORE_ID } : undefined);
  }
}
